import {WebCallService} from "@/utilities/system/services/WebCallService";
import {PathHelper} from "@/utilities/system/helpers/PathHelper";
import {ErrorLocationType, HttpType, PreferenceType, UsersType} from "@/utilities/system/enums/Enums";
import NullOrUndefinedHelper from "@/utilities/system/helpers/NullOrUndefinedHelper";
import {ChangePasswordRequest, UserRequest} from "@/models/api/requests";
import {User} from "@/models/entities/users/User";
import {UserWithAdditional} from "@/models/entities/users/UserWithAdditional";
import {UserPreference} from "@/models/system/user/UserPreference";
import {UserPreferenceRequest} from "@/models/system/api/Requests";
import ErrorHandler from "@/utilities/system/helpers/ErrorHandler";

export class UserService {
    private webCaller: WebCallService;
    private uriHelper: PathHelper;

    constructor(){
        this.webCaller = new WebCallService();
        this.uriHelper = new PathHelper();
    }

    public async getUsers(errorLocationType:ErrorLocationType):Promise<Array<User>|null> {
        const uri = this.uriHelper.users.getUsersPath();

        const resp = await this.webCaller.HttpSend(HttpType.get, uri);
        if (resp === null || resp === undefined || (resp.statusCode !== undefined && resp.statusCode !== 0)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async getUsersWithAdditionalData(errorLocationType:ErrorLocationType):Promise<Array<UserWithAdditional>|null> {
        const uri = this.uriHelper.users.getUsersWithAdditionalDataPath();

        const resp = await this.webCaller.HttpSend(HttpType.get, uri);
        if (resp === null || resp === undefined || (resp.statusCode !== undefined && resp.statusCode !== 0)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async getUserById(id:string, errorLocationType:ErrorLocationType):Promise<User|null> {
        const uri = this.uriHelper.users.getUserByIdPath(id);

        const resp = await this.webCaller.HttpSend(HttpType.get, uri);
        if (resp === null || resp === undefined || (resp.statusCode !== undefined && resp.statusCode !== 0)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async addOrUpdateUser(user:User, errorLocationType:ErrorLocationType):Promise<User|null> {
        const uri = NullOrUndefinedHelper(user.id)
            ? this.uriHelper.users.getUsersPath()
            : this.uriHelper.users.getUserByIdPath(user.id);

        const requestBody = new UserRequest(user);

        const reqType = NullOrUndefinedHelper(user.id)
            ? HttpType.post
            : HttpType.put;

        const resp = await this.webCaller.HttpSend(reqType, uri, requestBody);
        if (resp === null || resp === undefined || (resp.statusCode !== undefined && resp.statusCode !== 0)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async deleteUser(id:string, errorLocationType:ErrorLocationType):Promise<string|null> {
        const uri = this.uriHelper.users.getUserByIdPath(id);

        const resp = await this.webCaller.HttpSend(HttpType.delete, uri);
        if (resp === null || resp === undefined || (resp.statusCode !== undefined && resp.statusCode !== 0)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    /*
        when clearAll === true: either ALL preferences are cleared or JUST preferences of the specified table (tableName === "" or tableName === <smth>)
    */
    public async setUserPreferences(id: string,
                                    typeId: PreferenceType,
                                    tableName: string,
                                    clearAll: boolean,
                                    prefList: Array<UserPreference>,
                                    errorLocationType:ErrorLocationType):Promise<any|null> { //TODO type
        const uri = this.uriHelper.preferences.getSaveUserPreferencesPath();
        const requestBody = new UserPreferenceRequest(id, typeId, tableName, clearAll, prefList);

        const resp = await this.webCaller.HttpSend(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || (resp.statusCode !== undefined && resp.statusCode !== 0)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async getUserPreferences(id: string, typeId: PreferenceType, errorLocationType:ErrorLocationType):Promise<Array<any>|null> { //TODO type
        const uri = this.uriHelper.preferences.getUserPreferencePath(id, typeId);

        const resp = await this.webCaller.HttpSend(HttpType.get, uri);
        if (resp === null || resp === undefined || (resp.statusCode !== undefined && resp.statusCode !== 0)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async postUserCsvImport(file:File, usersType:UsersType, errorLocationType:ErrorLocationType):Promise<any|null> { //TODO type
        const uri = this.uriHelper.users.getUserImportPath(usersType);
        const requestBody = new FormData();
        requestBody.append("userCsvFile", file);

        const resp = await this.webCaller.HttpSend(HttpType.post, uri, requestBody);
        if (resp === null || resp === undefined || (resp.statusCode !== undefined && resp.statusCode !== 0)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp
    }

    public async putChangePassword(userId:string, current:string, changed:string, changedRepetition:string, errorLocationType:ErrorLocationType):Promise<any|null> {
        const uri = this.uriHelper.users.getChangePasswordPath();
        const requestBody = new ChangePasswordRequest(userId, current, changed, changedRepetition);

        const resp = await this.webCaller.HttpSend(HttpType.put, uri, requestBody);
        if (resp === null || resp === undefined || (resp.statusCode !== undefined && resp.statusCode !== 0)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp
    }
}